import React, { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    Skeleton,
    Stack,
} from "@mui/material";
import ReportIcon from "@mui/icons-material/Report";
import Icon from "src/components/icon";
import styled from "styled-components";
import github from "src/assets/icons/github";
import { Helmet } from "react-helmet";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import { useNavigate } from "react-router-dom";
import fetchStatus from "src/utils/api/fetch-status";
import { format } from "date-fns";
import { useActiveIntegrations } from "src/scenes/dashboard/active-integrations-provider";

const H1 = styled.h1`
    font-size: 22px;
    margin: 0;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const P = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 18px;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const P2 = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const PLabel = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 18px 18px 18px 0;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const CustomPaper = styled(Paper)`
    border-radius: 14px;
    width: 100%;
    cursor: pointer;
    transition: opacity 0.4s ease-out;
    position: static;
`;

const PaperTitle = styled.p`
    margin: 0;
    padding: 7px 0 6px 18px;
    font-size: 11px;
    background-color: #5b5b5b;
    border-radius: 14px 14px 0 0;
    backface-visibility: hidden;
    transform: translateZ(0) scale(1, 1);
`;

const PermissionsIcon = styled(Icon)`
    background-color: #fff;
    border-radius: 16px;
    padding: 24px 12px 24px 12px;
    margin: 32px 0 32px 0;
`;

const DisconnectButton = styled(Button)`
    width: 136px;
    height: 36px;
`;

const SettingsGithub = () => {
    const [settings, setSettings] = React.useState(undefined);
    const [disconnectSelected, isDisconnectSelected] = React.useState(false);

    const navigate = useNavigate();
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [centrallyManagedIntegrations, setCentrallyManagedIntegrations] = useState(false);

    const { activeIntegrations: userIntegrations } = useActiveIntegrations();

    const getSettings = async () => {
        const response = await request(`/api/github`, { method: "GET" });
        setSettings(response);
    };

    React.useEffect(() => {
        getSettings();
    }, []);

    React.useEffect(() => {
        if (userIntegrations.status === fetchStatus.SUCCESS) {
            const githubIntegration = userIntegrations.data.integrations.find(
                (item) => item.name === "github"
            );
            if (githubIntegration) {
                setCentrallyManagedIntegrations(!!githubIntegration.centrallyManaged);
            }
        }
    }, [userIntegrations.status]);

    const handleDisconnectChange = (event) => {
        isDisconnectSelected(event.target.checked);
    };

    const handleDisconnect = async () => {
        try {
            await request("/api/github/integration", { method: "DELETE" });
            userIntegrations.refetch();
            setMessage("Successfully disconnected github integration. We are sad to see you go..");
            setSeverity("success");
            setIsToastTriggered(true);
            navigate("/integrations");
        } catch (e) {
            setMessage("Failed to disconnect GitHub. Please try again.");
            setSeverity("warning");
            setIsToastTriggered(true);
        }
        return undefined;
    };
    // TODO Handle reconnect
    return (
        <>
            <Helmet>
                <title>Your GitHub settings</title>
            </Helmet>
            <Grid container justifyContent="center" mb={4}>
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    style={{ height: "100%", width: "70%" }}
                >
                    <Stack direction="row" alignItems="center">
                        <PermissionsIcon svg={github} width="100px" height="127" />
                        <Stack direction="column" ml={2}>
                            <H1>
                                GitHub Settings
                                {centrallyManagedIntegrations && <H1>(Centrally Managed)</H1>}
                            </H1>
                            <H2>settings for your GitHub integration</H2>
                        </Stack>
                    </Stack>
                    {settings && settings.invalid === true && (
                        <CustomPaper style={{ marginBottom: "14px", padding: "7px" }}>
                            <Stack direction="row" alignItems="center">
                                <Box
                                    style={{
                                        backgroundColor: "darkorange",
                                        borderRadius: "46px",
                                        margin: "18px",
                                        height: "46px",
                                        width: "46px",
                                    }}
                                >
                                    <ReportIcon style={{ margin: "11px", color: "#303030" }} />
                                </Box>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    style={{ width: "88%" }}
                                >
                                    <Stack direction="column">
                                        <P2 style={{ fontSize: "14px", marginBottom: "8px" }}>
                                            <strong>
                                                {settings.invalidationError} for{" "}
                                                {settings.githubUserId}
                                            </strong>
                                        </P2>
                                        <P2>{settings.invalidationReason}</P2>
                                        <P2>
                                            You might be viewing partial or incorrect data.
                                            Disconnect and reconnect your GitHub account.
                                        </P2>
                                    </Stack>
                                    {/* <Button onClick={handleReconnect}>Reconnect Github</Button> */}
                                </Stack>
                            </Stack>
                        </CustomPaper>
                    )}
                    <CustomPaper>
                        <PaperTitle>Integration Information</PaperTitle>
                        <>
                            <Stack direction="row" alignItems="center">
                                <P>
                                    <strong>Account created:</strong>{" "}
                                </P>
                                {settings ? (
                                    <P style={{ marginLeft: 0 }}>
                                        {format(
                                            new Date(settings.createdAt * 1000),
                                            "EEE d MMM yyyy"
                                        )}
                                    </P>
                                ) : (
                                    <Skeleton variant="rectangular" width={210} height={22} />
                                )}
                            </Stack>
                            <Divider />
                            <Stack direction="row" alignItems="center">
                                <P>
                                    <strong>GitHub UserId:</strong>
                                </P>
                                {settings ? (
                                    <P style={{ marginLeft: 0 }}>{settings.githubUserId}</P>
                                ) : (
                                    <Skeleton variant="rectangular" width={210} height={22} />
                                )}
                            </Stack>
                            <Divider />
                            <Stack direction="row" alignItems="center">
                                <P>
                                    <strong>Integration status:</strong>
                                </P>
                                {settings ? (
                                    <P style={{ marginLeft: 0 }}>
                                        {settings.integrationStatus || settings.invalidationError}
                                    </P>
                                ) : (
                                    <Skeleton variant="rectangular" width={210} height={22} />
                                )}
                            </Stack>
                            <Divider />
                            <Stack direction="row" alignItems="center">
                                <P>
                                    <strong>Selected scope:</strong>
                                </P>
                                {settings ? (
                                    <P style={{ marginLeft: 0 }}>{settings.scope}</P>
                                ) : (
                                    <Skeleton variant="rectangular" width={210} height={22} />
                                )}
                            </Stack>
                        </>
                    </CustomPaper>
                    <P style={{ fontSize: "16px", marginBottom: 0 }}>Disconnect GitHub</P>
                    <Stack
                        direction="row"
                        alignItems="center"
                        style={{ width: "100%", padding: "0px 16px" }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={disconnectSelected}
                                    onChange={handleDisconnectChange}
                                />
                            }
                            label={
                                <PLabel>
                                    Disconnect integration Adadot integration with GitHub and remove
                                    data.
                                </PLabel>
                            }
                        />
                        {disconnectSelected && (
                            <DisconnectButton variant="contained" onClick={handleDisconnect}>
                                Disconnect
                            </DisconnectButton>
                        )}
                    </Stack>
                </Stack>
            </Grid>
        </>
    );
};

export default SettingsGithub;
